import './styles.scss'

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Col, Empty, Row } from 'antd'

import B2BecLink from '@/components/B2BLink'
import CustomButton from '@/components/CustomButton'
import { useAppSelector } from '@/store/hook'
import {
  selectSubmenuAccessoriesAndDetergents,
  selectSubmenuCategories,
} from '@/store/selectors/categorySelector'
import { selectIsFuturetech } from '@/store/selectors/configSelector'
import { selectShowSparePartLink } from '@/store/selectors/userSelector'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { IMAGE_SOURCES } from '@/libs/constants'
import { MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'
import futureTechAccessoriesImage from '@/assets/img/futuretech/accessories.webp'
import futureTechDetergentsImage from '@/assets/img/futuretech/detergents.webp'
import sparePartsImage from '@/assets/img/spare-parts.webp'

import getImageUrl from '../getImageUrl'

function HeaderSubmenu({ setShowSubmenu }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const productMenuRef = useRef(null)
  const shouldShowSparePart = useAppSelector(selectShowSparePartLink)
  const categories = useAppSelector(selectSubmenuCategories)
  const accessoriesAndDetergents = useAppSelector(
    selectSubmenuAccessoriesAndDetergents,
  )
  const isFutureTech = useAppSelector(selectIsFuturetech)
  const localeUpperCase = i18n.language?.toUpperCase()?.split('-')[1]

  const handleClickLink = () => {
    setShowSubmenu(false)
  }

  const handleSeeCategoryOverview = () => {
    setShowSubmenu(false)
    navigate(linkGenerator('/categories'))
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        productMenuRef.current
        && !productMenuRef.current.contains(event.target)
      ) {
        setShowSubmenu(false)
      }
    }

    document.addEventListener('click', handleClickOutside, true)

    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [setShowSubmenu])

  const renderSubmenuCategories = () => {
    if (isValidArray(categories?.subCategories)) {
      return (
        <div
          key={categories?.categoryId}
          className="header-submenu__item__wrapper"
        >
          <Row>
            <Col className="header-submenu__title" span={24}>
              <h3 className="header-submenu__title-heading">
                {categories?.categoryName}
              </h3>
            </Col>
          </Row>
          <Row gutter={10}>
            {categories?.subCategories?.map((subCategory) =>
              subCategory?.categoryName && subCategory?.categoryId
                ? (
                  <Col
                    key={subCategory?.categoryId}
                    className="header-submenu__item"
                    lg={4}
                    md={6}
                    sm={8}
                    xl={4}
                    xs={12}
                  >
                    <B2BecLink
                      className="header-submenu__item__link"
                      to={`/category/${subCategory?.categoryId}`}
                      style={{
                        backgroundImage: getImageUrl(subCategory?.menuImages),
                      }}
                      onClick={handleClickLink}
                    >
                      {subCategory?.categoryName}
                    </B2BecLink>
                  </Col>
                )
                : null
            )}
          </Row>
        </div>
      )
    }

    return <Empty description={t('noData')} />
  }

  const getCategoryDetails = ({ idx, isFutureTech }) => {
    const isAccessories = idx === 0
    const imageType = isAccessories ? 'ACCESSORIES' : 'DETERGENTS'

    let imageSource = IMAGE_SOURCES[localeUpperCase]?.[imageType]
      || IMAGE_SOURCES.DEFAULT[imageType]

    if (isFutureTech) {
      imageSource = isAccessories
        ? futureTechAccessoriesImage
        : futureTechDetergentsImage
    }

    return {
      to: isAccessories ? '/categories/accessories' : '/categories/detergents',
      translatedCategoryName: isAccessories
        ? t('searchResultsPage.header.accessories')
        : t('searchResultsPage.header.detergents'),
      imageSrc: `url(${imageSource})`,
    }
  }

  const renderSubmenuAccessoriesAndDetergents = () => {
    if (isValidArray(accessoriesAndDetergents)) {
      return accessoriesAndDetergents?.map((category, index) => {
        const { to, translatedCategoryName, imageSrc } = getCategoryDetails({
          idx: index,
          isFutureTech,
        })

        return (
          <Col
            key={category?.categoryId}
            className="header-submenu__item"
            lg={4}
            md={6}
            sm={8}
            xl={4}
            xs={12}
          >
            <B2BecLink
              className="header-submenu__item__link"
              to={to}
              style={{
                backgroundImage: imageSrc,
              }}
              onClick={handleClickLink}
            >
              {translatedCategoryName}
            </B2BecLink>
          </Col>
        )
      })
    }

    return <Empty description={t('noData')} />
  }

  return (
    <div ref={productMenuRef} className="header-submenu fadeInDown">
      <div className="header-submenu__container">
        {renderSubmenuCategories()}
        <Row>
          <Col className="header-submenu__title" span={24}>
            <h3 className="header-submenu__title-heading">
              {t('proEquipment')}
            </h3>
          </Col>
        </Row>
        <Row gutter={10}>
          {renderSubmenuAccessoriesAndDetergents()}
          {shouldShowSparePart && (
            <PermissionWrapper
              permission={MODULE_SHOP_NAVIGATION_RIGHTS.OPEN_SPARE_PART_SHOP}
            >
              <Col
                className="header-submenu__item"
                lg={4}
                md={6}
                sm={8}
                xl={4}
                xs={12}
              >
                <B2BecLink
                  className="header-submenu__item__link"
                  to="/spare-parts"
                  style={{
                    backgroundImage: `url(${sparePartsImage})`,
                  }}
                  onClick={handleClickLink}
                >
                  {t('searchResultsPage.header.spareParts')}
                </B2BecLink>
              </Col>
            </PermissionWrapper>
          )}
        </Row>
        <p className="header-submenu__bottom-link">
          <CustomButton
            className="header-submenu__bottom-link-button"
            type="ghost"
            onClick={handleSeeCategoryOverview}
          >
            {t('categoryOverviewPage')}
          </CustomButton>
        </p>
      </div>
    </div>
  )
}

export default HeaderSubmenu
